import type { useTranslation } from "@sagot/i18n";
import { exitName } from "./builder/step-summary";
import { exitSlotsOf, type ExitSlot } from "./exit-slots";
import type { Definition, Step } from "./types";

type TFunction = ReturnType<typeof useTranslation>["t"];

const H = "engage.workflows.extras.history";

/** The step as the author knows it: the name they gave it, else its kind. */
function nameOfStep(step: Step, t: TFunction): string {
  const own = step.name?.trim();
  if (own) return own;
  return t(`engage.workflows.steps.${step.type}`);
}

/** "Path D · Tour". */
function titleOfPath(definition: Definition, id: string, t: TFunction): string {
  const label = definition.paths[id]?.label;
  return `${t("engage.workflows.builder.path", { id })}${label ? ` · ${label}` : ""}`;
}

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** The first exit whose destination differs between two versions of one step. */
function changedExit(before: Step, after: Step): { slot: ExitSlot; was: string | undefined } | null {
  const was = new Map(exitSlotsOf(before).map((slot) => [slot.key, slot.target]));
  for (const slot of exitSlotsOf(after)) if (was.has(slot.key) && was.get(slot.key) !== slot.target) return { slot, was: was.get(slot.key) };
  return null;
}

/** What happened to the steps of one path whose list changed. */
function stepListEdit(before: Definition, after: Definition, pathId: string, t: TFunction): string | null {
  const was = before.paths[pathId]?.steps ?? [];
  const now = after.paths[pathId]?.steps ?? [];
  const path = titleOfPath(after, pathId, t);
  const wasIds = was.map((step) => step.id);
  const nowIds = now.map((step) => step.id);
  if (now.length > was.length) {
    const added = now.find((step) => !wasIds.includes(step.id));
    return added ? t(`${H}.addStep`, { step: nameOfStep(added, t), path }) : null;
  }
  if (now.length < was.length) {
    const removed = was.find((step) => !nowIds.includes(step.id));
    return removed ? t(`${H}.removeStep`, { step: nameOfStep(removed, t), path }) : null;
  }
  const replacedAt = was.findIndex((step, index) => step.id !== now[index]?.id);
  const replacement = now[replacedAt];
  const original = was[replacedAt];
  if (replacedAt < 0 || !replacement || !original) return null;
  if (wasIds.includes(replacement.id)) return t(`${H}.moveStep`, { step: nameOfStep(replacement, t) });
  return t(`${H}.replaceStep`, { from: nameOfStep(original, t), to: nameOfStep(replacement, t) });
}

/** One step of one path was edited: a connection, or any other field. */
function stepEdit(before: Definition, after: Definition, pathId: string, t: TFunction): string | null {
  const was = before.paths[pathId]?.steps ?? [];
  const now = after.paths[pathId]?.steps ?? [];
  const index = now.findIndex((step, position) => !sameJson(step, was[position]));
  const step = now[index];
  const earlier = was[index];
  if (!step || !earlier) return null;
  const exit = changedExit(earlier, step);
  if (!exit) return t(`${H}.editStep`, { step: nameOfStep(step, t) });
  const name = exitName(exit.slot, t);
  if (exit.slot.target && exit.slot.target in after.paths) return t(`${H}.connect`, { exit: name, path: titleOfPath(after, exit.slot.target, t) });
  return t(`${H}.disconnect`, { exit: name });
}

function pathSetEdit(before: Definition, after: Definition, t: TFunction): string | null {
  const wasIds = Object.keys(before.paths);
  const nowIds = Object.keys(after.paths);
  const added = nowIds.filter((id) => !wasIds.includes(id));
  const removed = wasIds.filter((id) => !nowIds.includes(id));
  const [created] = added;
  if (removed.length === 1 && added.length === 0) return t(`${H}.deletePath`, { path: titleOfPath(before, removed[0] ?? "", t) });
  if (added.length !== 1 || removed.length > 0 || !created) return null;
  const connected = nowIds.map((id) => stepEdit(before, after, id, t)).find((label): label is string => label !== null);
  return connected ?? t(`${H}.addPath`, { path: titleOfPath(after, created, t) });
}

function labelEdit(before: Definition, after: Definition, t: TFunction): string | null {
  const id = Object.keys(after.paths).find((candidate) => (before.paths[candidate]?.label ?? "") !== (after.paths[candidate]?.label ?? ""));
  if (id === undefined) return null;
  return t(`${H}.renamePath`, { path: titleOfPath(after, id, t) });
}

function notesEdit(before: Definition, after: Definition, t: TFunction): string | null {
  const wasCount = Object.keys(before.notes ?? {}).length;
  const nowCount = Object.keys(after.notes ?? {}).length;
  if (sameJson(before.notes, after.notes)) return null;
  if (nowCount > wasCount) return t(`${H}.addNote`);
  return nowCount < wasCount ? t(`${H}.deleteNote`) : t(`${H}.editNote`);
}

/**
 * The key under which the keystrokes of one translation merge into a single undo step: an edit that changed nothing but
 * one language's text for one step. Anything else has no key of its own (its caller may give one).
 */
export function coalesceKeyOf(before: Definition, after: Definition): string | undefined {
  if (before.translations === after.translations || before.paths !== after.paths || before.settings !== after.settings) return undefined;
  for (const [language, byStep] of Object.entries(after.translations ?? {})) {
    const stepId = Object.keys(byStep).find((id) => !sameJson(byStep[id], before.translations?.[language]?.[id]));
    if (stepId !== undefined) return `translation:${language}:${stepId}`;
  }
  return undefined;
}

/**
 * A short phrase for what an edit did, by comparing the definition before and after it ("connect Book a tour to Path D",
 * "delete Path C · Tuition"). It is what Undo and Redo name in their tooltips, so nobody has to guess.
 */
export function describeEdit(before: Definition, after: Definition, t: TFunction): string {
  const pathLevel = pathSetEdit(before, after, t) ?? labelEdit(before, after, t);
  if (pathLevel) return pathLevel;
  for (const id of Object.keys(after.paths)) {
    const was = before.paths[id]?.steps ?? [];
    const now = after.paths[id]?.steps ?? [];
    if (sameJson(was, now)) continue;
    const label = was.length === now.length && sameJson(was.map((s) => s.id), now.map((s) => s.id)) ? stepEdit(before, after, id, t) : stepListEdit(before, after, id, t);
    if (label) return label;
  }
  const notes = notesEdit(before, after, t);
  if (notes) return notes;
  if (!sameJson(before.settings, after.settings)) return t(`${H}.settings`);
  if (!sameJson(before.translations, after.translations)) return t(`${H}.translations`);
  return t(`${H}.generic`);
}
